import React, { useRef, useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Button,
  IconButton,
  TextField,
  Paper,
  Box,
  Stack,
  Alert,
  Chip,
  MenuItem,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import BlockIcon from "@mui/icons-material/Block";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import * as XLSX from "xlsx";
import ButtonComponent from "../../../../CommonComponents/common/button/Button";
import "./DomainActionModal.css";
import { domainActionModalStyles } from "./constants/domainActionModalStyles";
import {
  ADD_DOMAIN_TITLE, ADD_DOMAIN_FIELD_LABEL, ADD_DOMAIN_PLACEHOLDER,
  BTN_BACK, BTN_CLEAR_ALL, BTN_SAVE,
  UPLOAD_TITLE, UPLOAD_DESCRIPTION, UPLOAD_BROWSE_LABEL, UPLOAD_DRAG_DROP,
  UPLOAD_SUPPORTS, UPLOAD_TEMPLATE_FILE, UPLOAD_TEMPLATE_SHEET,
  UPLOAD_FORMAT_LABEL, UPLOAD_COLUMN_NAME, UPLOAD_EXAMPLE_DOMAIN,
  BTN_DOWNLOAD_TEMPLATE, BTN_CANCEL, BTN_UPLOAD_DOMAINS,
  WHITELIST_TITLE, WHITELIST_DESCRIPTION, WHITELIST_DOMAINS_LABEL,
  WHITELIST_APPLICATION_LABEL, WHITELIST_APPLICATION_PLACEHOLDER,
  WHITELIST_APPLICATION_OPTIONS,
  WHITELIST_JUSTIFICATION_LABEL, WHITELIST_JUSTIFICATION_PLACEHOLDER,
  BTN_SUBMIT,
  BLACKLIST_TITLE, BLACKLIST_DESCRIPTION, BLACKLIST_WARNING, BLACKLIST_DOMAINS_LABEL,
  BLACKLIST_REQUESTED_FOR, BLACKLIST_REASON_LABEL, BLACKLIST_REASON_PLACEHOLDER,
  BLACKLIST_DEFAULT_REASON, BTN_CONFIRM, HELPER_TEXT
} from "./constants/domainActionModalText";

// action: "add" | "upload" | "whitelist" | "blacklist"
const DomainActionModal = ({
  action,
  open,
  onClose,
  domains = [],
  requestedBy,
  onSubmit,
  onUpload,
}) => {
  if (!open) return null;

  if (action === "add") {
    return (
      <AddDomainContent
        open={open}
        onClose={onClose}
        onSubmit={onSubmit}
      />
    );
  }

  if (action === "upload") {
    return (
      <UploadDomainContent
        open={open}
        onClose={onClose}
        onUpload={onUpload}
      />
    );
  }

  if (action === "whitelist") {
    return (
      <WhitelistDomainContent
        open={open}
        onClose={onClose}
        domains={domains}
        onSubmit={onSubmit}
      />
    );
  }

  if (action === "blacklist") {
    return (
      <BlacklistDomainContent
        open={open}
        onClose={onClose}
        domains={domains}
        requestedBy={requestedBy}
        onSubmit={onSubmit}
      />
    );
  }

  return null;
};

const AddDomainContent = ({ open, onClose, onSubmit }) => {
  const [domainName, setDomainName] = useState("");

  useEffect(() => {
    if (open) setDomainName("");
  }, [open]);

  const handleClearAll = () => setDomainName("");

  const handleCancel = () => {
    handleClearAll();
    onClose();
  };

  const handleSubmit = () => {
    onSubmit({ domainName: domainName.trim() });
  };

  return (
    <Dialog open={open} onClose={handleCancel} fullWidth maxWidth="sm">
      <DialogTitle sx={domainActionModalStyles.dialogTitleRow}>
        {ADD_DOMAIN_TITLE}
        <IconButton onClick={handleCancel}><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ px: 1, pt: 1 }}>
          <Typography fontWeight={700} mb={1}>{ADD_DOMAIN_FIELD_LABEL}</Typography>
          <TextField
            fullWidth
            placeholder={ADD_DOMAIN_PLACEHOLDER}
            value={domainName}
            onChange={(e) => setDomainName(e.target.value)}
            helperText={HELPER_TEXT}
            FormHelperTextProps={{
            sx: {
            ml: 0,
            },
            }}
            sx={{ "& .MuiInputBase-root": { height: 40 } }}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <div className="btn-wrapper_domainAction" style={{ gap: 10 }}>
          <ButtonComponent label={BTN_BACK} type="primary" maxWidth="80px" onClick={handleCancel} />
          <ButtonComponent label={BTN_CLEAR_ALL} type="primary" maxWidth="120px" onClick={handleClearAll} />
          <ButtonComponent
            label={BTN_SAVE}
            type="secondary"
            maxWidth="100px"
            onClick={handleSubmit}
            disabled={!domainName.trim()}
          />
        </div>
      </DialogActions>
    </Dialog>
  );
};

const UploadDomainContent = ({ open, onClose, onUpload }) => {
  const fileInputRef = useRef();
  const [selectedFile, setSelectedFile] = useState(null);

  const handleBrowse = () => fileInputRef.current.click();

  const handleFileChange = (e) => {
    if (e.target.files.length > 0) setSelectedFile(e.target.files[0]);
  };

  const handleClose = () => {
    setSelectedFile(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
    onClose();
  };

  const downloadTemplate = () => {
    const worksheet = XLSX.utils.aoa_to_sheet([[UPLOAD_COLUMN_NAME]]);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, UPLOAD_TEMPLATE_SHEET);
    XLSX.writeFile(workbook, UPLOAD_TEMPLATE_FILE);
  };

  const handleUpload = () => {
    if (!selectedFile) return;
    onUpload(selectedFile);
    setSelectedFile(null);
    handleClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle sx={domainActionModalStyles.dialogTitleRow}>
        {UPLOAD_TITLE}
        <IconButton onClick={handleClose}><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent>
        <Typography color="text.secondary" mb={3}>
          {UPLOAD_DESCRIPTION}
        </Typography>
        <Paper
          variant="outlined"
          sx={domainActionModalStyles.uploadDropzone}
          onClick={handleBrowse}
        >
          <CloudUploadOutlinedIcon sx={domainActionModalStyles.uploadIcon} />
          <Typography mt={2} fontWeight={600}>{UPLOAD_BROWSE_LABEL}</Typography>
          <Typography variant="body2" color="text.secondary">{UPLOAD_DRAG_DROP}</Typography>
          <Typography mt={1} fontSize={12} color="text.secondary">{UPLOAD_SUPPORTS}</Typography>
          <input hidden ref={fileInputRef} type="file" accept=".csv,.xls,.xlsx" onChange={handleFileChange} />
        </Paper>
        {selectedFile && <Alert severity="success" sx={{ mt: 2 }}>{selectedFile.name}</Alert>}
        <Box mt={2} sx={domainActionModalStyles.uploadInfoBox}>
          <InfoOutlinedIcon sx={domainActionModalStyles.infoIcon} />
          <Box flex={1}>
            <Typography variant="body2" sx={{ color: "#4b5563", lineHeight: 1.6 }}>
              <strong>{UPLOAD_FORMAT_LABEL}</strong> Column header{" "}
              <Box component="span" sx={domainActionModalStyles.codeLabel}>
                {UPLOAD_COLUMN_NAME}
              </Box>{" "}
              in row 1, one domain per row below (e.g. <strong>{UPLOAD_EXAMPLE_DOMAIN}</strong>).
            </Typography>
            <Button
              size="small"
              startIcon={<DownloadOutlinedIcon />}
              onClick={downloadTemplate}
              sx={domainActionModalStyles.downloadTemplateBtn}
            >
              {BTN_DOWNLOAD_TEMPLATE}
            </Button>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>{BTN_CANCEL}</Button>
        <Button
          variant="contained"
          onClick={handleUpload}
          disabled={!selectedFile}
          sx={domainActionModalStyles.primaryActionButton}
        >
          {BTN_UPLOAD_DOMAINS}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const WhitelistDomainContent = ({ open, onClose, domains = [], onSubmit }) => {
  const [reason, setReason] = useState("");
  const [applicationType, setApplicationType] = useState("");
  const loggedInUser = localStorage.getItem("userEmail") ?? "";

  const handleApplicationChange = (selectedApplicationType) => {
    const selectedApplication = WHITELIST_APPLICATION_OPTIONS.find(
      (option) => option.value === selectedApplicationType
    );

    setApplicationType(selectedApplicationType);
    setReason(selectedApplication?.businessJustification || "");
  };

  const handleSubmit = () => {
    onSubmit({
      domains: domains.map((d) => d.domain),
      user: loggedInUser,
      justification: reason,
      applicationType,
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={domainActionModalStyles.dialogTitleRow}>
        {WHITELIST_TITLE}
        <IconButton onClick={onClose}><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent>
        <Typography color="text.secondary" mb={2}>
          {WHITELIST_DESCRIPTION}
        </Typography>
        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>
          {WHITELIST_DOMAINS_LABEL} ({domains.length})
        </Typography>
        <Paper variant="outlined">
          {domains.map((item) => (
            <Stack
              key={item.domain}
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              sx={domainActionModalStyles.domainListRow}
            >
              <Stack direction="row" spacing={1} alignItems="center">
                <CheckCircleOutlineIcon color="success" fontSize="small" />
                <Typography fontWeight={600}>{item.domain}</Typography>
              </Stack>
              <Typography variant="caption" color="text.secondary">{item.requestDate}</Typography>
            </Stack>
          ))}
        </Paper>
        <Box mt={3}>
          <Typography fontWeight={600} mb={1}>{WHITELIST_APPLICATION_LABEL}</Typography>
          <TextField
            select
            required
            fullWidth
            value={applicationType}
            onChange={(e) => handleApplicationChange(e.target.value)}
            SelectProps={{ displayEmpty: true }}
          >
            <MenuItem value="" disabled>
              {WHITELIST_APPLICATION_PLACEHOLDER}
            </MenuItem>
            {WHITELIST_APPLICATION_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
        </Box>
        <Box mt={3}>
          <Typography fontWeight={600} mb={1}>{WHITELIST_JUSTIFICATION_LABEL}</Typography>
          <TextField
            fullWidth multiline rows={4} value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder={WHITELIST_JUSTIFICATION_PLACEHOLDER}
          />
          <Typography variant="caption" color="text.secondary">{reason.length} characters</Typography>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} sx={domainActionModalStyles.ghostCancelButton}>
          {BTN_CANCEL}
        </Button>
        <Button
          variant="contained"
          disabled={!applicationType || !reason.trim()}
          onClick={handleSubmit}
          sx={domainActionModalStyles.primaryActionButton}
        >
          {BTN_SUBMIT}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const BlacklistDomainContent = ({ open, onClose, domains = [], requestedBy, onSubmit }) => {
  const [reason, setReason] = useState(BLACKLIST_DEFAULT_REASON);

  const handleClose = () => {
    setReason("");
    onClose();
  };

  const handleSubmit = () => {
    onSubmit({
      domains: domains.map((d) => d.domain),
      user: requestedBy,
      justification: reason,
    });
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={domainActionModalStyles.dialogTitleRow}>
        {BLACKLIST_TITLE}
        <IconButton onClick={handleClose}><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent>
        <Typography color="text.secondary" mb={2}>
          {BLACKLIST_DESCRIPTION}
        </Typography>
        <Alert severity="warning" icon={<WarningAmberRoundedIcon />} sx={{ mb: 3 }}>
          {BLACKLIST_WARNING}
        </Alert>
        <Typography fontWeight={700} mb={1}>
          {BLACKLIST_DOMAINS_LABEL} ({domains.length})
        </Typography>
        <Paper variant="outlined">
          {domains.map((item) => (
            <Stack
              key={item.domain}
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              sx={domainActionModalStyles.domainListRow}
            >
              <Stack direction="row" spacing={1} alignItems="center">
                <BlockIcon color="error" fontSize="small" />
                <Typography fontWeight={600}>{item.domain}</Typography>
              </Stack>
              <Chip label={item.status} size="small" color="default" />
            </Stack>
          ))}
        </Paper>
        <Box mt={3}>
          <Typography fontWeight={600}>{BLACKLIST_REQUESTED_FOR}</Typography>
          <Paper variant="outlined" sx={domainActionModalStyles.requestedForPaper}>
            <Typography variant="body2" color="text.secondary">{requestedBy}</Typography>
          </Paper>
        </Box>
        <Box mt={3}>
          <Typography fontWeight={600} mb={1}>{BLACKLIST_REASON_LABEL}</Typography>
          <TextField
            fullWidth multiline rows={4} value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder={BLACKLIST_REASON_PLACEHOLDER}
          />
          <Typography variant="caption" color="text.secondary">{reason.length} characters</Typography>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} sx={domainActionModalStyles.ghostCancelButton}>
          {BTN_CANCEL}
        </Button>
        <Button
          variant="contained"
          disabled={!reason.trim()}
          onClick={handleSubmit}
          sx={domainActionModalStyles.primaryActionButton}
        >
          {BTN_CONFIRM}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DomainActionModal;
